import { useEffect, useMemo, useRef, useState } from 'react';
import { usePlatformChatMessageAttachmentsQuery } from '../../../features/chat/queries/usePlatformChatMessageAttachmentsQuery';
import { isAiMessage } from '../../../types/chatMessage';
import {
  hasAttachmentId,
  isAssistantImageAttachment,
  isImageAttachment,
} from './chatMessageAttachmentUtils';
import { LiksChatMessageBubbleContent } from './LiksChatMessageBubbleContent';
import { LiksChatMessageSources } from './LiksChatMessageSources';
import { LiksChatMessageTrailingActions } from './LiksChatMessageTrailingActions';
import type { LiksChatMessageProps } from './LiksChatMessage.types';
import './LiksChatMessage.css';

export type { LiksChatMessageProps } from './LiksChatMessage.types';

export function LiksChatMessage({
  message,
  formattedTime,
  copyLabel,
  copiedLabel,
  retryLabel,
  downloadImageLabel,
  downloadAttachmentLabel,
  expandImageLabel,
  closeImagePreviewLabel,
  imageLoadingLabel,
  assistantReplyingLabel,
  generatedImageAltLabel,
  imageGenerationFailedLabel,
  enableSaveToGallery = false,
  saveToGalleryLabel,
  sourcesLabel,
  sourcesEmptyLabel,
  getSourceTypeLabel,
  openDocumentLabel,
  openTrainingExampleLabel,
  documentPreviewCloseLabel,
  documentPreviewLoadingLabel,
  documentPreviewErrorLabel,
  documentPreviewInaccessibleLabel,
  documentPreviewUnavailableLabel,
  sourceTypeColumnLabel,
  sourceFileColumnLabel,
  sourceUrlColumnLabel,
  sourceInaccessibleLabel,
  sourceTrainingDeletedLabel,
  showRetry = false,
  disableRetry = false,
  onRetry,
  isCurrentUser = false,
  enableSources = true,
  enableAttachments = true,
  fallbackKnowledgeBaseId,
  knowledgeBases,
  sourcesHint,
  onOpenTrainingExample,
  renderDocumentPreview,
  showImprove = false,
  improveLabel,
  onImprove,
}: LiksChatMessageProps) {
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef<number | null>(null);
  const isAi = isAiMessage(message);
  const showLoadingSpinner = isAi && message.isPendingAssistantReply === true;
  const isStreamingReply =
    isAi &&
    message.isStreamingAssistantReply === true &&
    message.isPendingAssistantReply !== true;
  const displayContent =
    isAi &&
    message.imageGenerationFailed === true &&
    imageGenerationFailedLabel != null &&
    imageGenerationFailedLabel.length > 0
      ? imageGenerationFailedLabel
      : message.content;
  const displayMessage =
    displayContent === message.content
      ? message
      : { ...message, content: displayContent };
  const hasTextContent = displayContent.trim().length > 0;
  const sources = message.sources ?? [];
  const shouldFetchAttachments =
    enableAttachments &&
    message.hasAttachment === true &&
    message.imageGenerationFailed !== true;

  const attachmentsQuery = usePlatformChatMessageAttachmentsQuery(
    message.sessionId,
    message.id,
    { enabled: shouldFetchAttachments },
  );
  const messageAttachments = useMemo(
    () => (attachmentsQuery.data ?? []).filter(hasAttachmentId),
    [attachmentsQuery.data],
  );
  const assistantImageAttachments = useMemo(
    () =>
      messageAttachments.filter((attachment) =>
        isAssistantImageAttachment(attachment),
      ),
    [messageAttachments],
  );
  const userImageAttachments = useMemo(
    () =>
      messageAttachments.filter((attachment) => isImageAttachment(attachment)),
    [messageAttachments],
  );
  const userFileAttachments = useMemo(
    () =>
      messageAttachments.filter((attachment) => !isImageAttachment(attachment)),
    [messageAttachments],
  );
  const visibleUserAttachments = isAi ? [] : messageAttachments;
  const hasVisibleAttachments = isAi
    ? assistantImageAttachments.length > 0
    : visibleUserAttachments.length > 0;

  const waitingForEmptyAssistantAttachments =
    shouldFetchAttachments &&
    isAi &&
    !showLoadingSpinner &&
    !hasTextContent &&
    !message.id.startsWith('local-') &&
    attachmentsQuery.isLoading;

  const showSources =
    enableSources &&
    isAi &&
    !showLoadingSpinner &&
    !waitingForEmptyAssistantAttachments;

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current !== null) {
        window.clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(displayContent);
      setCopied(true);

      if (copyTimeoutRef.current !== null) {
        window.clearTimeout(copyTimeoutRef.current);
      }

      copyTimeoutRef.current = window.setTimeout(() => {
        setCopied(false);
      }, 1500);
    } catch {
      setCopied(false);
    }
  };

  const showCopy =
    !showLoadingSpinner &&
    !waitingForEmptyAssistantAttachments &&
    hasTextContent;
  const showActionsFooter =
    showSources || (showRetry && isAi) || showCopy || showImprove;

  const trailingActions = (
    <LiksChatMessageTrailingActions
      showRetry={showRetry}
      isAi={isAi}
      retryLabel={retryLabel}
      onRetry={onRetry}
      disableRetry={disableRetry}
      showImprove={showImprove}
      improveLabel={improveLabel}
      onImprove={onImprove}
      showCopy={showCopy}
      copied={copied}
      copiedLabel={copiedLabel}
      copyLabel={copyLabel}
      onCopy={() => {
        void handleCopy();
      }}
    />
  );

  return (
    <article
      className={[
        'liks-chat-message',
        isAi
          ? 'liks-chat-message--ai'
          : isCurrentUser
            ? 'liks-chat-message--self'
            : 'liks-chat-message--participant',
      ].join(' ')}
    >
      <div
        className={[
          'liks-chat-message__bubble',
          isAi &&
            !hasTextContent &&
            !showLoadingSpinner &&
            assistantImageAttachments.length > 0 &&
            'liks-chat-message__bubble--image-only',
          !isAi &&
            !hasTextContent &&
            hasVisibleAttachments &&
            'liks-chat-message__bubble--attachments-only',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <header className="liks-chat-message__meta">
          <span className="liks-chat-message__author">
            {message.author.name}
          </span>
          <time className="liks-chat-message__time" dateTime={message.sentAt}>
            {formattedTime}
          </time>
        </header>

        {showLoadingSpinner || waitingForEmptyAssistantAttachments ? (
          <div
            className="liks-chat-message__spinner"
            aria-label={assistantReplyingLabel}
          />
        ) : (
          <LiksChatMessageBubbleContent
            message={displayMessage}
            isAi={isAi}
            hasTextContent={hasTextContent}
            isStreamingReply={isStreamingReply}
            assistantImageAttachments={assistantImageAttachments}
            userImageAttachments={userImageAttachments}
            userFileAttachments={userFileAttachments}
            generatedImageAltLabel={generatedImageAltLabel}
            downloadImageLabel={downloadImageLabel}
            downloadAttachmentLabel={downloadAttachmentLabel}
            expandImageLabel={expandImageLabel}
            closeImagePreviewLabel={closeImagePreviewLabel}
            imageLoadingLabel={imageLoadingLabel}
            enableSaveToGallery={enableSaveToGallery}
            saveToGalleryLabel={saveToGalleryLabel}
          />
        )}

        {showActionsFooter && (
          <footer className="liks-chat-message__actions">
            {showSources ? (
              <LiksChatMessageSources
                sources={sources}
                sourcesLabel={sourcesLabel}
                sourcesEmptyLabel={sourcesEmptyLabel}
                sourceTypeColumnLabel={sourceTypeColumnLabel}
                sourceFileColumnLabel={sourceFileColumnLabel}
                sourceUrlColumnLabel={sourceUrlColumnLabel}
                sourceInaccessibleLabel={sourceInaccessibleLabel}
                getSourceTypeLabel={getSourceTypeLabel}
                openDocumentLabel={openDocumentLabel}
                openTrainingExampleLabel={openTrainingExampleLabel}
                documentPreviewCloseLabel={documentPreviewCloseLabel}
                documentPreviewLoadingLabel={documentPreviewLoadingLabel}
                documentPreviewErrorLabel={documentPreviewErrorLabel}
                documentPreviewInaccessibleLabel={
                  documentPreviewInaccessibleLabel
                }
                documentPreviewUnavailableLabel={
                  documentPreviewUnavailableLabel
                }
                sourceTrainingDeletedLabel={sourceTrainingDeletedLabel}
                fallbackKnowledgeBaseId={fallbackKnowledgeBaseId}
                knowledgeBases={knowledgeBases}
                sourcesHint={sourcesHint}
                onOpenTrainingExample={onOpenTrainingExample}
                renderDocumentPreview={renderDocumentPreview}
                trailingActions={trailingActions}
              />
            ) : (
              <div className="liks-chat-message__actions-row liks-chat-message__actions-row--end">
                <div className="liks-chat-message__actions-end">
                  {trailingActions}
                </div>
              </div>
            )}
          </footer>
        )}
      </div>
    </article>
  );
}
